import axios from 'axios';
import Vue from 'vue';
import Vuex from 'vuex';

axios.defaults.xsrfCookieName = 'csrftoken';
axios.defaults.xsrfHeaderName = 'X-CSRFTOKEN';

Vue.use(Vuex);

const apiURL = process.env.VUE_APP_API_URL;

export default new Vuex.Store({
  state: {
    qaid: null,
    logo: null,
    battery: null,
    introText: null,
    continueButton: null,
    skipButton: null,
    submitButton: null,
    closedText: null,
    title: null,
    startNode: null,
    nodes: {},
    currentNode: null,
    theme: {},
    variables: {},
    customFields: {},
    chainOptionId: null,
  },
  mutations: {
    LOAD_QUALIFIER: (state, qualifier) => {
      Vue.set(state, 'qaid', qualifier.qaid);
      Vue.set(state, 'logo', qualifier.logo);
      Vue.set(state, 'introText', qualifier.intro_text);
      Vue.set(state, 'continueButton', qualifier.continue_button);
      Vue.set(state, 'skipButton', qualifier.skip_button);
      Vue.set(state, 'submitButton', qualifier.submit_button);
      Vue.set(state, 'closedText', qualifier.closed_text);
      Vue.set(state, 'title', qualifier.page_title);
      Vue.set(state, 'battery', qualifier.battery);
      if (qualifier.battery !== null) {
        qualifier.battery.nodes.forEach((node) => {
          Vue.set(state.nodes, node.id, node);
        });
        Vue.set(state, 'startNode', state.nodes[qualifier.battery.initial_node]);
      }
      Vue.set(state, 'theme', {
        dark: qualifier.dark_theme,
        backgroundColor: qualifier.background_color,
        primaryColor: qualifier.primary_color,
        secondaryColor: qualifier.secondary_color,
        accentColor: qualifier.accent_color,
        errorColor: qualifier.error_color,
        infoColor: qualifier.info_color,
        successColor: qualifier.success_color,
        warningColor: qualifier.warning_color,
      });
      document.title = qualifier.page_title;
    },
    SELECT_FIRST_NODE: (state) => {
      Vue.set(state, 'currentNode', state.nodes[state.startNode.links[0].target]);
    },
    NEXT_NODE: (state, { optionId, linkType }) => {
      let node = null;
      let processing = true;
      let nextOptionId = optionId;
      let nextLinkType = linkType;
      let nextLink = null;

      while (processing) {
        processing = false;

        // if nextLinkType is defined, follow it
        if (
          nextLinkType === 'skipped' ||
          nextLinkType === 'out' ||
          nextLinkType === 'passed' ||
          nextLinkType === 'failed'
        ) {
          const nlt = nextLinkType; // keep the linter happy
          nextLink = state.currentNode.links.find((link) => link.link_type === nlt);

          // otherwise, follow nextOptionId
        } else if (nextOptionId !== null) {
          if (Array.isArray(nextOptionId)) {
            let nl = nextLink; //  keep the linter happy
            nextOptionId.forEach((item) => {
              if (nl === null || nl === undefined) {
                nl = state.currentNode.links.find((link) => link.option === item);
              }
            });
            nextLink = nl;
          } else {
            const noi = nextOptionId; // keep the linter happy
            nextLink = state.currentNode.links.find((link) => link.option === noi);
          }
        }

        if (nextLink === null || nextLink === undefined) {
          nextLink = state.currentNode.links.find((link) => link.link_type === 'default');
        }

        nextOptionId = null;
        nextLinkType = null;
        node = state.nodes[nextLink?.target];

        if (node.node_type === 'study') {
          const url = `${document.location.protocol}//${document.location.host}/s/${node.study}/${document.location.search}`;
          document.location.replace(url);
        } else if (node.node_type === 'balancer') {
          let maxCount = Number.POSITIVE_INFINITY;
          let studies = [];
          node.links.forEach((link) => {
            const studyNode = state.nodes[link.target];
            if (studyNode.study_count === maxCount) {
              studies.push(studyNode);
            } else if (studyNode.study_count < maxCount) {
              maxCount = studyNode.study_count;
              studies = [studyNode];
            }
          });

          const studyNode = studies[Math.floor(Math.random() * studies.length)];
          const url = `${document.location.protocol}//${document.location.host}/s/${studyNode.study}/${document.location.search}`;
          document.location.replace(url);
        } else if (node.node_type === 'operation') {
          let value = 0;
          if (state.variables[node.variable] !== undefined) {
            value = state.variables[node.variable];
          }
          if (node.operation === 'set') {
            value = node.value;
          } else if (node.operation === 'add') {
            value += node.value;
          } else if (node.opertation === 'multiply') {
            value *= node.value;
          }
          state.variables = Object.assign(state.variables, { [node.variable]: value });
          Vue.set(state, 'currentNode', node);
          nextLinkType = 'out';
          processing = true;
        } else if (node.node_type === 'comparison') {
          let value = 0;
          let passed = false;
          if (state.variables[node.variable] !== undefined) {
            value = state.variables[node.variable];
          }
          if (node.comparison === 'eq') {
            passed = value === node.value;
          } else if (node.comparison === 'ne') {
            passed = value !== node.value;
          } else if (node.comparison === 'gt') {
            passed = value > node.value;
          } else if (node.comparison === 'lt') {
            passed = value < node.value;
          }
          Vue.set(state, 'currentNode', node);
          nextLinkType = passed ? 'passed' : 'failed';
          processing = true;
        } else if (node.node_type === 'contains') {
          let passed = false;
          if (node.custom_field && node.valid_set && state.customFields[node.custom_field]) {
            // eslint-disable-next-line no-loop-func
            passed = String(state.customFields[node.custom_field]).split('|').some((v) => node.valid_set.includes(v));
          }
          Vue.set(state, 'currentNode', node);
          nextLinkType = passed ? 'passed' : 'failed';
          processing = true;
        } else if (node.node_type === 'support') {
          // do nothing, can't handle support during qualification
          Vue.set(state, 'currentNode', node);
          nextLinkType = 'out';
          processing = true;
        } else {
          Vue.set(state, 'currentNode', node);
          window.scrollTo(0, 0);
        }
      }
    },
    SET_PRODUCT_LABEL: (state, productLabel) => {
      Vue.set(state, 'productLabel', productLabel);
    },
    STORE_CUSTOM_FIELD: (state, { name, value }) => {
      const update = { ...state.customFields };
      update[name] = value;
      state.customFields = update;
    },
  },
  actions: {
    loadQualifier(context) {
      const el = document.getElementById('__package__');
      const data = el.textContent;
      context.commit('LOAD_QUALIFIER', JSON.parse(data));
      document.getElementById('loading').style.display = 'none';
    },
    selectFirstNode(context) {
      context.commit('SELECT_FIRST_NODE');
    },
    nextNode(context, optionId) {
      context.commit('NEXT_NODE', { optionId, linkType: null });
    },
    skipNode(context) {
      context.commit('NEXT_NODE', { optionId: null, linkType: 'skipped' });
    },
    processLabel(context, info) {
      const payload = new FormData();
      payload.append('hash_id', context.state.hashId);
      payload.append('question', info.node.question.id);
      payload.append('image', info.image);
      axios
        .post(`${apiURL}api/productlabel/`, payload, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then((res) => {
          context.commit('SET_PRODUCT_LABEL', res.data);
        })
        .catch(() => {
          context.commit('SET_PRODUCT_LABEL', {});
        });
    },
    submitResponse(context, info) {
      const payload = {
        qaid: context.state.qaid,
        node: info.node.id,
        battery: context.state.battery.id,
        question: info.node.question.id,
        text: info.text,
        value: info.value,
        values: info.values,
      };
      if (info.node.custom_field) {
        let value = null;
        // this is messy and terrible, but works for now
        // just remember that text overrules all
        if (info.value !== undefined && info.value !== null) {
          value = info.value;
        }
        if (info.values !== undefined && info.values !== null) {
          value = info.values;
        }
        if (info.text !== undefined && info.text !== null) {
          value = info.text;
        }
        context.commit('STORE_CUSTOM_FIELD', { name: info.node.custom_field, value });
      }
      axios
        .post(`${apiURL}api/response/`, payload)
        .then(() => {
          context.dispatch('nextNode', info.id);
        })
        .catch(() => {
          // eslint-disable-next-line
          alert('There was a problem submitting your response. Please reload this page.');
        });
    },
  },
  modules: {},
});
